import { useState } from 'react';
import { zapPaths } from '../../zapPaths';
import { Input } from '../../../../../../components/tailwind-component/ui/input';
import { Button } from '../../../../../../components/tailwind-component/ui/button';
import { Badge } from '../../../../../../components/tailwind-component/ui/badge';
import { Plus, X, Link, AlertCircle, Loader2, Zap, FileText, FileSpreadsheet, Presentation, File, FileArchive, ClipboardList, FolderOpen } from 'lucide-react';
import { Autocomplete, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import { ZapLinkProduct } from 'custom-type';
import { InstagramDMAutomation } from '../../../../../../core/services/dm-automation-instagram.service';
import { BASE_URL as ZAP_SHORT_LINK_BASE_URL } from '../../../../../../config/config';
import { useNavigate } from 'react-router-dom';
import FileUploadPopup from '../../../../../../components/popup/file-upload-popup';
import { formatEntitlementLimit } from '../../../../../../core/utility';

const zapService = new InstagramDMAutomation();

interface ZapLinkSectionProps {
  zapLinks: ZapLinkProduct[];
  setZapLinks: (links: ZapLinkProduct[]) => void;
  maxZapProducts: number;
  handleOpenUpgradePopup: (content: React.ReactNode) => void;
  onClose: () => void;
  currentPlanName: string | null;
  allowOtherSource: boolean;
  productImageUploadPath: string;
}


const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const zapLinkDisplayImageUrl = (link: ZapLinkProduct): string =>
  (link.manual_product_image_url || link.product_image_url || '').trim();

/* A document link has no product photo to show. Rather than the empty grey box
   this used to render, show an icon that says what kind of file it is. Google
   Drive files still get a real first-page thumbnail from the API, so this is the
   fallback for everything else (raw PDFs, Notion, Forms, ...). */
const DOC_ICONS: Record<string, { Icon: any; tint: string }> = {
  google_doc:   { Icon: FileText,        tint: 'text-blue-500' },
  doc:          { Icon: FileText,        tint: 'text-blue-500' },
  pages:        { Icon: FileText,        tint: 'text-orange-500' },
  txt:          { Icon: FileText,        tint: 'text-gray-500' },
  notion:       { Icon: FileText,        tint: 'text-gray-700' },
  google_sheet: { Icon: FileSpreadsheet, tint: 'text-green-600' },
  xls:          { Icon: FileSpreadsheet, tint: 'text-green-600' },
  csv:          { Icon: FileSpreadsheet, tint: 'text-green-600' },
  numbers:      { Icon: FileSpreadsheet, tint: 'text-green-500' },
  google_slide: { Icon: Presentation,    tint: 'text-amber-500' },
  ppt:          { Icon: Presentation,    tint: 'text-amber-500' },
  key:          { Icon: Presentation,    tint: 'text-sky-500' },
  pdf:          { Icon: File,            tint: 'text-red-500' },
  zip:          { Icon: FileArchive,     tint: 'text-slate-500' },
  google_form:  { Icon: ClipboardList,   tint: 'text-violet-500' },
  google_drive: { Icon: FolderOpen,      tint: 'text-amber-500' },
  dropbox:      { Icon: FolderOpen,      tint: 'text-blue-500' },
};

const DocThumb = ({ docType, size }: { docType: string; size: string }) => {
  const entry = DOC_ICONS[docType] || { Icon: File, tint: 'text-gray-400' };
  const { Icon, tint } = entry;
  return (
    <div className={`${size} bg-gray-50 rounded flex items-center justify-center shrink-0 border`}>
      <Icon className={`w-5 h-5 ${tint}`} />
    </div>
  );
};

const applyFetchResult = (base: ZapLinkProduct, data: any, fallbackUrl: string): ZapLinkProduct => ({
  ...base,
  product_url: data.product_url || fallbackUrl,
  product_name: data.product_name || '',
  product_image_url: data.product_image_url || '',
  doc_type: data.doc_type || undefined,
  manual_product_image_url: undefined,
  manual_product_image_file_name: undefined,
  manual_product_image_file_type: undefined,
  manual_product_image_file_path: undefined,
  manual_product_image_storage_size: undefined,
  brand_name: data.brand_name || '',
  price: data.price ?? null,
  currency: data.currency || '',
  discount_text: data.discount_text || '',
  og_description: data.og_description || '',
  og_type: data.og_type || '',
  og_image_width: data.og_image_width || '',
  og_image_height: data.og_image_height || '',
  og_locale: data.og_locale || '',
  fetch_status: 'success',
});

const currencyOptions = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'INR', label: 'INR — Indian Rupee' },
  { value: 'CNY', label: 'CNY — Chinese Yuan' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { value: 'KRW', label: 'KRW — South Korean Won' },
  { value: 'THB', label: 'THB — Thai Baht' },
  { value: 'VND', label: 'VND — Vietnamese Dong' },
  { value: 'IDR', label: 'IDR — Indonesian Rupiah' },
  { value: 'MYR', label: 'MYR — Malaysian Ringgit' },
  { value: 'PHP', label: 'PHP — Philippine Peso' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'NZD', label: 'NZD — New Zealand Dollar' },
  { value: 'AED', label: 'AED — UAE Dirham' },
  { value: 'SAR', label: 'SAR — Saudi Riyal' },
  { value: 'CHF', label: 'CHF — Swiss Franc' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'BRL', label: 'BRL — Brazilian Real' },
  { value: 'MXN', label: 'MXN — Mexican Peso' },
  { value: 'ZAR', label: 'ZAR — South African Rand' },
];

export function ZapLinkSection({ zapLinks, setZapLinks, maxZapProducts, handleOpenUpgradePopup, onClose, currentPlanName, allowOtherSource, productImageUploadPath }: ZapLinkSectionProps) {
  const [productUrl, setProductUrl] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [manualEditIndex, setManualEditIndex] = useState<number | null>(null);
  const [showProductImageUpload, setShowProductImageUpload] = useState(false);
  const [manualData, setManualData] = useState({
    product_name: '',
    brand_name: '',
    price: '',
    currency: '',
    discount_text: '',
    manual_product_image_url: '',
    manual_product_image_file_name: '',
    manual_product_image_file_type: '',
    manual_product_image_file_path: '',
    manual_product_image_storage_size: '' as string | number,
  });
  const navigate = useNavigate();

  const handleFetchProduct = async () => {
    const trimmedUrl = productUrl.trim();
    if (!trimmedUrl) { setUrlError('Please enter a product URL'); return; }
    if (!isValidUrl(trimmedUrl)) { setUrlError('Please enter a valid URL starting with http:// or https://'); return; }

    // If limit is 0 or not set, show upgrade popup
    if (!maxZapProducts) {
      // onClose();
      
      handleOpenUpgradePopup(
        <>
          Zap Link is not available on your current plan.{' '}
          <span
            className="underline text-blue-600 cursor-pointer hover:text-blue-800"
            onClick={() => navigate(zapPaths().subscription)}
          >
            Upgrade
          </span>{' '}
          to unlock product link sharing via DM.
        </>
      );
      return;
    }

    if (zapLinks.some(link => link.product_url === trimmedUrl)) { setUrlError('This product URL has already been added'); return; }
    if (zapLinks.filter(l => l.fetch_status !== 'failed').length >= maxZapProducts) {
      // onClose();
      handleOpenUpgradePopup(
        <>
          You can add up to {formatEntitlementLimit(maxZapProducts)} product {maxZapProducts === 1 ? 'link' : 'links'} per rule on your current plan.
          
          {/* {
            {' '}
            <span
              className="underline text-blue-600 cursor-pointer hover:text-blue-800"
              onClick={() => navigate(zapPaths().subscription)}
            >
              Upgrade
            </span>{' '}
            to add more.
          } */}

          {
            currentPlanName === 'Savings'
            ?
            <span></span>
            :
            <>
              {' '}
              <span
                className="underline text-blue-600 cursor-pointer hover:text-blue-800"
                onClick={() => navigate(zapPaths().subscription)}
              >
                Upgrade
              </span>{' '}
              to add more.
            </>
          }
        </>
      );
      return;
    }

    setUrlError('');
    setIsFetching(true);
    setProductUrl('');

    const pendingProduct: ZapLinkProduct = {
      product_url: trimmedUrl, original_url: trimmedUrl, allow_other_source: allowOtherSource, fetch_status: 'pending', status: 'Live', is_active: true, sort_order: zapLinks.length,
    };
    const withPending = [...zapLinks, pendingProduct];
    setZapLinks(withPending);

    try {
      const res = await zapService.fetchZapLinkProduct({ product_url: trimmedUrl, allow_other_source: allowOtherSource });
      const updated = [...withPending];
      if (res?.status === 1 && res?.data) {
        updated[updated.length - 1] = applyFetchResult(pendingProduct, res.data, trimmedUrl);
      } else {
        updated[updated.length - 1] = { ...pendingProduct, fetch_status: 'failed' };
      }
      setZapLinks(updated);
    } catch {
      const updated = [...withPending];
      updated[updated.length - 1] = { ...pendingProduct, fetch_status: 'failed' };
      setZapLinks(updated);
    } finally {
      setIsFetching(false);
    }
  };

  const handleRetry = async (index: number) => {
    const link = zapLinks[index];
    if (!link) return;
    setIsFetching(true);
    const updated = [...zapLinks];
    updated[index] = { ...link, fetch_status: 'pending' };
    setZapLinks(updated);
    try {
      const res = await zapService.fetchZapLinkProduct({ product_url: link.product_url, allow_other_source: allowOtherSource });
      const result = [...zapLinks];
      if (res?.status === 1 && res?.data) {
        result[index] = applyFetchResult(link, res.data, link.product_url);
      } else {
        result[index] = { ...link, fetch_status: 'failed' };
        toast.error(res?.info || "Couldn't fetch product details.");
      }
      setZapLinks(result);
    } catch {
      const result = [...zapLinks];
      result[index] = { ...link, fetch_status: 'failed' };
      setZapLinks(result);
      toast.error("Retry failed. Please try again.");
    } finally {
      setIsFetching(false);
    }
  };

  const handleRemoveProduct = (index: number) => {
    setZapLinks(zapLinks.filter((_, i) => i !== index));
  };

  const handleManualAdd = (index: number) => {
    const link = zapLinks[index];
    setManualData({
      product_name: link.product_name || '',
      brand_name: link.brand_name || '',
      price: link.price?.toString() || '',
      currency: link.currency || '',
      discount_text: link.discount_text || '',
      manual_product_image_url: link.manual_product_image_url || '',
      manual_product_image_file_name: link.manual_product_image_file_name || '',
      manual_product_image_file_type: link.manual_product_image_file_type || '',
      manual_product_image_file_path: link.manual_product_image_file_path || '',
      manual_product_image_storage_size: link.manual_product_image_storage_size ?? '',
    });
    setManualEditIndex(index);
    setShowProductImageUpload(false);
  };

  const handleSaveManual = () => {
    if (manualEditIndex === null) return;
    const hasPrice = manualData.price.trim() !== '';
    if (!manualData.manual_product_image_url.trim()) {
      toast.error('Please upload a product image');
      return;
    }
    if (hasPrice && !manualData.currency.trim()) {
      toast.error('Please select a currency');
      return;
    }
    const updated = [...zapLinks];
    updated[manualEditIndex] = {
      ...updated[manualEditIndex],
      product_name: manualData.product_name,
      brand_name: manualData.brand_name,
      price: manualData.price ? parseFloat(manualData.price) : null,
      currency: manualData.currency,
      discount_text: manualData.discount_text,
      product_image_url: '',
      manual_product_image_url: manualData.manual_product_image_url,
      manual_product_image_file_name: manualData.manual_product_image_file_name || undefined,
      manual_product_image_file_type: manualData.manual_product_image_file_type || undefined,
      manual_product_image_file_path: manualData.manual_product_image_file_path || undefined,
      manual_product_image_storage_size:
        manualData.manual_product_image_storage_size === '' || manualData.manual_product_image_storage_size === undefined
          ? undefined
          : manualData.manual_product_image_storage_size,
      fetch_status: 'success'
    };
    setZapLinks(updated);
    setManualEditIndex(null);
    setShowProductImageUpload(false);
    setManualData({
      product_name: '',
      brand_name: '',
      price: '',
      currency: '',
      discount_text: '',
      manual_product_image_url: '',
      manual_product_image_file_name: '',
      manual_product_image_file_type: '',
      manual_product_image_file_path: '',
      manual_product_image_storage_size: '',
    });
  };

  const handleCancelManual = () => {
    setManualEditIndex(null);
    setShowProductImageUpload(false);
    setManualData({
      product_name: '',
      brand_name: '',
      price: '',
      currency: '',
      discount_text: '',
      manual_product_image_url: '',
      manual_product_image_file_name: '',
      manual_product_image_file_type: '',
      manual_product_image_file_path: '',
      manual_product_image_storage_size: '',
    });
  };

  return (
    <div className="space-y-4">
      {/* URL Input */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            placeholder="https://brand.com/product/..."
            value={productUrl}
            onChange={(e) => { setProductUrl(e.target.value); if (urlError) setUrlError(''); }}
            onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleFetchProduct(); } }}
            disabled={isFetching}
          />
          <Button
            type="button"
            variant="outline"
            className="btn btn-outline-primary shrink-0"
            onClick={handleFetchProduct}
            disabled={!productUrl.trim() || isFetching}
          >
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
        {urlError && (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <AlertCircle className="w-3 h-3 shrink-0" />{urlError}
          </p>
        )}
        <p className="text-xs text-gray-500">
          {/* OLD (pre-unlimited): "Add up to {maxZapProducts} products per rule." — an
              unlimited plan resolves maxZapProducts to Infinity, so the cap sentence is dropped. */}
          Paste a product URL — we'll fetch the details.
          {Number.isFinite(maxZapProducts) ? ` Add up to ${maxZapProducts} products per rule.` : ''}
        </p>
      </div>

      {/* Product Cards */}
      {zapLinks.length > 0 && (
        <div className="space-y-2">
          {zapLinks.map((link, index) => (
            <div key={index} className="border rounded-lg p-3 bg-white overflow-hidden w-full">

              {link.fetch_status === 'pending' ? (
                /* Loading skeleton */
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-100 rounded animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-gray-100 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
                    <p className="text-xs text-gray-400 break-all leading-snug">{link.product_url}</p>
                  </div>
                </div>

              ) : link.fetch_status === 'failed' ? (
                /* Failed state */
                <div>
                  {manualEditIndex === index ? (
                    /* Manual edit form */
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-800">Add Product Details Manually</h4>
                        <button type="button" onClick={handleCancelManual} className="text-gray-400 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        <Input
                          placeholder="Product Name"
                          value={manualData.product_name}
                          onChange={(e) => setManualData(prev => ({ ...prev, product_name: e.target.value }))}
                        />
                        <Input
                          placeholder="Brand Name"
                          value={manualData.brand_name}
                          onChange={(e) => setManualData(prev => ({ ...prev, brand_name: e.target.value }))}
                        />
                        <div className="flex gap-2">
                          <Input
                            className='flex-1'
                            placeholder="Price"
                            type="number"
                            value={manualData.price}
                            onChange={(e) => setManualData(prev => ({ ...prev, price: e.target.value }))}
                          />
                          <Autocomplete
                            className='flex-1'
                            options={currencyOptions}
                            getOptionLabel={(option) => option.label}
                            value={manualData.currency ? currencyOptions.find(opt => opt.value === manualData.currency) || null : null}
                            onChange={(event, newValue) => {
                              setManualData(prev => ({ ...prev, currency: newValue?.value || '' }));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Currency"
                                required={manualData.price.trim() !== ''}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            disablePortal
                            sx={{ flex: 1 }}
                          />
                        </div>
                        <Input
                          placeholder="Discount Text (optional)"
                          value={manualData.discount_text}
                          onChange={(e) => setManualData(prev => ({ ...prev, discount_text: e.target.value }))}
                        />
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-700">
                            Product image <span className="text-red-500">*</span>
                          </p>
                          <div className="flex items-start gap-3">
                            {manualData.manual_product_image_url ? (
                              <img
                                src={manualData.manual_product_image_url}
                                alt="Product preview"
                                className="w-16 h-16 object-cover rounded border shrink-0"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-16 h-16 bg-gray-50 rounded border shrink-0 flex items-center justify-center">
                                <Link className="w-5 h-5 text-gray-300" />
                              </div>
                            )}
                            <div className="flex flex-col gap-2 min-w-0">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-fit shrink-0"
                                onClick={() => setShowProductImageUpload(true)}
                              >
                                Upload image
                              </Button>
                              {!manualData.manual_product_image_url.trim() && (
                                <p className="text-xs text-amber-600">Upload a JPG or PNG (required).</p>
                              )}
                            </div>
                          </div>
                          {showProductImageUpload && (
                            <FileUploadPopup
                              disablePortal
                              coverImage={false}
                              openModal={showProductImageUpload}
                              onClose={() => setShowProductImageUpload(false)}
                              onSend={(e: { url: string; key: string; fileName: string; fileType: string; storage_size: number | string }) =>
                                setManualData((prev) => ({
                                  ...prev,
                                  manual_product_image_url: e.url,
                                  manual_product_image_file_name: e.fileName,
                                  manual_product_image_file_type: e.fileType,
                                  manual_product_image_file_path: e.key,
                                  manual_product_image_storage_size: e.storage_size,
                                }))
                              }
                              path={productImageUploadPath}
                              acceptedFileTypes=".jpg, .jpeg, .png"
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveManual}
                          disabled={
                            !manualData.product_name.trim() ||
                            !manualData.manual_product_image_url.trim() ||
                            (manualData.price.trim() !== '' && !manualData.currency.trim())
                          }
                        >
                          Save Details
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCancelManual}
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400 break-all leading-snug">{link.product_url}</p>
                    </div>
                  ) : (
                    /* Default failed state */
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gray-50 rounded flex items-center justify-center shrink-0 border">
                        <Link className="w-5 h-5 text-gray-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                          <AlertCircle className="w-3 h-3 shrink-0" />
                          Couldn't fetch details automatically.
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5 break-all leading-snug">{link.product_url}</p>
                        <div className="flex gap-3 mt-1.5">
                          <button
                            type="button"
                            onClick={() => handleRetry(index)}
                            disabled={isFetching}
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                          >
                            Retry
                          </button>
                          <button
                            type="button"
                            onClick={() => handleManualAdd(index)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Manual Add
                          </button>
                        </div>
                      </div>
                      <button type="button" onClick={() => handleRemoveProduct(index)} className="text-gray-400 hover:text-red-500 shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

              ) : (
                /* Success state */
                <div className="flex items-start gap-3">
                  {/* doc_type is checked FIRST, ahead of any image.

                      OLD: the image branch came first, so a document that HAD an
                      image url (the Drive page thumbnail we used to return, or a
                      row saved before that was removed) rendered the page render
                      instead of the icon — and for an ordinary text document that
                      render is a white page, i.e. an empty bordered box. A
                      document's identity is its file type, so the icon wins. */}
                  {link.doc_type ? (
                    <DocThumb docType={link.doc_type} size="w-12 h-12" />
                  ) : zapLinkDisplayImageUrl(link) ? (
                    <img
                      src={zapLinkDisplayImageUrl(link)}
                      alt={link.product_name || 'Product'}
                      className="w-12 h-12 object-cover rounded border shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '';
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-50 rounded flex items-center justify-center shrink-0 border">
                      <Link className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-sm font-medium text-gray-800 line-clamp-2">
                      {link.product_name || 'Product'}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {link.brand_name && <span className="text-xs text-gray-500">{link.brand_name}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {link.discount_text && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">{link.discount_text}</Badge>
                      )}
                      {link.price != null && (
                        <span className="text-xs text-gray-600">
                          {link.currency ? `${link.currency} ${link.price}` : link.price}
                        </span>
                      )}
                    </div>
                    {link.short_url ? (
                      <div className="flex items-center gap-1 mt-0.5">
                        {!link.allow_other_source && <Zap className="w-3 h-3 text-yellow-500 shrink-0" />}
                        <a href={link.short_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 mt-0.5 break-all leading-snug">
                          {link.short_url}
                        </a>
                      </div>
                    ) : (
                      <a href={link.processed_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 mt-0.5 break-all leading-snug">
                        {link.processed_url || link.product_url}
                      </a>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveProduct(index)}
                    className="text-gray-400 hover:text-red-500 shrink-0 mt-0.5"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {zapLinks.length === 0 && (
        <div className="text-center py-6 border border-dashed rounded-lg">
          <Link className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-xs text-gray-400">Add product links to share with users via DM</p>
        </div>
      )}
    </div>
  );
}
